import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number
  locale: string
}

const CURRENCIES: Record<string, CurrencyInfo> = {
  MX: { code: 'MXN', symbol: '$', rate: 20.2, locale: 'es-MX' },
  CO: { code: 'COP', symbol: '$', rate: 4200, locale: 'es-CO' },
  AR: { code: 'ARS', symbol: '$', rate: 950, locale: 'es-AR' },
  CL: { code: 'CLP', symbol: '$', rate: 950, locale: 'es-CL' },
  PE: { code: 'PEN', symbol: 'S/', rate: 3.75, locale: 'es-PE' },
  BR: { code: 'BRL', symbol: 'R$', rate: 5.0, locale: 'pt-BR' },
  ES: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'es-ES' },
  FR: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'fr-FR' },
  DE: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'de-DE' },
  IT: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'it-IT' },
  GB: { code: 'GBP', symbol: '£', rate: 0.79, locale: 'en-GB' },
  US: { code: 'USD', symbol: '$', rate: 1, locale: 'en-US' },
  CA: { code: 'CAD', symbol: 'CA$', rate: 1.36, locale: 'en-CA' },
  UY: { code: 'UYU', symbol: '$U', rate: 42, locale: 'es-UY' },
  EC: { code: 'USD', symbol: '$', rate: 1, locale: 'es-EC' },
  VE: { code: 'USD', symbol: '$', rate: 1, locale: 'es-VE' },
  GT: { code: 'GTQ', symbol: 'Q', rate: 7.8, locale: 'es-GT' },
  CR: { code: 'CRC', symbol: '₡', rate: 520, locale: 'es-CR' },
  DO: { code: 'DOP', symbol: 'RD$', rate: 58, locale: 'es-DO' },
  PA: { code: 'USD', symbol: '$', rate: 1, locale: 'es-PA' },
}

const DEFAULT_CURRENCY: CurrencyInfo = {
  code: 'USD',
  symbol: '$',
  rate: 1,
  locale: 'en-US',
}

const CACHE_KEY = 'anti_impulso_currency'

interface UseCurrencyReturn {
  currency: CurrencyInfo
  formatPrice: (usdAmount: number) => string
  formatPriceShort: (usdAmount: number) => string
  loading: boolean
}

export function useCurrency(): UseCurrencyReturn {
  const [currency, setCurrency] = useState<CurrencyInfo>(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY)
      if (cached) return JSON.parse(cached)
    } catch {}
    return DEFAULT_CURRENCY
  })
  const [loading, setLoading] = useState(() => {
    try {
      return !sessionStorage.getItem(CACHE_KEY)
    } catch {
      return true
    }
  })

  useEffect(() => {
    try {
      if (sessionStorage.getItem(CACHE_KEY)) return
    } catch {}

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000)

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        const countryCode = data?.country_code || ''
        const detected = CURRENCIES[countryCode] || DEFAULT_CURRENCY
        setCurrency(detected)
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(detected))
        } catch {}
      })
      .catch(() => {
        // Fallback: try navigator language
        const lang = navigator.language || ''
        if (lang.startsWith('es-MX') || lang === 'es-MX') {
          setCurrency(CURRENCIES.MX)
        }
      })
      .finally(() => {
        clearTimeout(timeout)
        setLoading(false)
      })

    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [])

  const formatPrice = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate
    // For currencies with large values, no decimals
    if (currency.rate >= 100) {
      return `${currency.symbol}${Math.round(converted).toLocaleString(currency.locale)} ${currency.code}`
    }
    return `${currency.symbol}${converted.toFixed(2)} ${currency.code}`
  }

  const formatPriceShort = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate
    if (currency.rate >= 100) {
      return `${currency.symbol}${Math.round(converted).toLocaleString(currency.locale)}`
    }
    return `${currency.symbol}${converted.toFixed(2)}`
  }

  return { currency, formatPrice, formatPriceShort, loading }
}
